import { useEffect, useMemo, useRef, useState } from 'react';

function CustomSelect({
  disabled = false,
  id,
  name,
  onChange,
  options = [],
  placeholder = 'Select an option',
  required = false,
  value = '',
}) {
  const rootRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);

  const normalizedOptions = useMemo(
    () => options.map((option) => String(option || '').trim()).filter(Boolean),
    [options]
  );
  const selectedIndex = normalizedOptions.findIndex((option) => option === value);
  const selectedLabel = selectedIndex >= 0 ? normalizedOptions[selectedIndex] : '';

  useEffect(() => {
    const closeOnOutsideClick = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('pointerdown', closeOnOutsideClick);
    return () => document.removeEventListener('pointerdown', closeOnOutsideClick);
  }, []);

  const selectOption = (option) => {
    onChange(option);
    setIsOpen(false);
  };

  const handleTriggerKeyDown = (event) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setIsOpen(true);
    }

    if (event.key === 'Escape') {
      setIsOpen(false);
    }
  };

  return (
    <div className={`custom-select ${isOpen ? 'is-open' : ''}`} ref={rootRef}>
      <input name={name} type="hidden" value={value} />
      <button
        aria-controls={`${id}-listbox`}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        className={`custom-select__trigger ${selectedLabel ? '' : 'is-placeholder'}`}
        disabled={disabled}
        id={id}
        onClick={() => setIsOpen((current) => !current)}
        onKeyDown={handleTriggerKeyDown}
        role="combobox"
        type="button"
      >
        <span>{selectedLabel || placeholder}</span>
        <span aria-hidden="true" className="custom-select__arrow" />
      </button>

      {isOpen ? (
        <div className="custom-select__panel" id={`${id}-listbox`} role="listbox">
          <button
            aria-selected={!value}
            className={`custom-select__option ${!value ? 'is-selected' : ''}`}
            onClick={() => selectOption('')}
            role="option"
            type="button"
          >
            {placeholder}
          </button>
          {normalizedOptions.map((option, index) => (
            <button
              aria-selected={option === value}
              className={`custom-select__option ${option === value ? 'is-selected' : ''}`}
              key={`${option}-${index}`}
              onClick={() => selectOption(option)}
              role="option"
              type="button"
            >
              {option}
            </button>
          ))}
          {required && normalizedOptions.length === 0 ? (
            <span className="custom-select__empty">No options configured</span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export default CustomSelect;
